import { memo } from "react";
import { AuditLogChangeType } from "@common/types/auditLog";
import { TransactionRuleAction } from "@common/types/transactionRule";
import { TransactionRuleActions } from "@components/transactionRules/TransactionRuleActions";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import {
    FieldDiffItem,
    RuleDetailsRowData,
    RuleItemSegment,
} from "../utils/historyRuleDetailsRows";
import styles from "../HistoryRuleDetails.module.scss";

const TextLines: React.FC<{ lines: string[]; className?: string }> = ({
    lines,
    className,
}) => (
    <>
        {lines.map((line, i) => (
            <div key={i} className={className ?? styles.textLine}>
                {line}
            </div>
        ))}
    </>
);

function extractRawIds(value: unknown): unknown {
    if (Array.isArray(value)) {
        const hasIdName = value.some(
            (item) => item && typeof item === "object" && "id" in item,
        );
        if (hasIdName) {
            return value.map((item) =>
                item && typeof item === "object" && "id" in item
                    ? (item as { id: unknown }).id
                    : item,
            );
        }
        return value;
    }
    if (value && typeof value === "object" && "id" in value) {
        return (value as { id: unknown }).id;
    }
    return value;
}

function snapshotToAction(
    snapshot: unknown,
    index: number,
): TransactionRuleAction | null {
    if (!snapshot || typeof snapshot !== "object") {
        return null;
    }
    const obj = snapshot as Record<string, unknown>;
    if (!("actionType" in obj)) {
        return null;
    }
    return {
        id: `snap-act-${index}`,
        actionType: obj.actionType,
        value: extractRawIds(obj.value),
        transferDirection: (obj.transferDirection as string) ?? null,
    } as TransactionRuleAction;
}

function renderSnapshotAction(
    snapshot: unknown,
    index: number,
    row: RuleDetailsRowData,
): React.ReactNode {
    const action = snapshotToAction(snapshot, index);
    if (!action) {
        return null;
    }
    return (
        <TransactionRuleActions
            rule={{ actions: [action] }}
            counterparties={row.counterparties}
        />
    );
}

const ActionFieldDiffItems: React.FC<{
    items: FieldDiffItem[];
    row: RuleDetailsRowData;
}> = ({ items, row }) => (
    <>
        {items.map((item, i) => {
            const key = `${row.versionId}-afd-${i}`;
            if (item.kind === "stable") {
                return (
                    <div key={key}>
                        {renderSnapshotAction(
                            item.after ?? item.before,
                            i,
                            row,
                        )}
                    </div>
                );
            }
            if (item.kind === "added") {
                return (
                    <div key={key} className={styles.addedItem}>
                        {renderSnapshotAction(item.after, i, row)}
                    </div>
                );
            }
            if (item.kind === "removed") {
                return (
                    <div key={key} className={styles.removed}>
                        {renderSnapshotAction(item.before, i, row)}
                    </div>
                );
            }
            return (
                <div key={key} className={styles.diffPair}>
                    <div className={styles.removed}>
                        {renderSnapshotAction(item.before, i, row)}
                    </div>
                    <div className={styles.addedItem}>
                        {renderSnapshotAction(item.after, i, row)}
                    </div>
                </div>
            );
        })}
    </>
);

function renderActionSegment(
    row: RuleDetailsRowData,
    segment: RuleItemSegment<TransactionRuleAction>,
    index: number,
): React.ReactNode {
    const baseKey = `${row.versionId}-act-${index}`;
    if (segment.kind === "stable") {
        return (
            <TransactionRuleActions
                key={baseKey}
                rule={{ actions: segment.items }}
                counterparties={row.counterparties}
            />
        );
    }
    if (segment.kind === "changed") {
        return (
            <div key={baseKey} className={styles.diffPair}>
                <div className={styles.removed}>
                    <TransactionRuleActions
                        rule={{ actions: [segment.before] }}
                        counterparties={row.counterparties}
                    />
                </div>
                <TransactionRuleActions
                    rule={{ actions: [segment.after] }}
                    counterparties={row.counterparties}
                />
            </div>
        );
    }
    if (segment.kind === "added") {
        return (
            <TransactionRuleActions
                key={baseKey}
                rule={{ actions: segment.items }}
                counterparties={row.counterparties}
            />
        );
    }
    return (
        <div key={baseKey} className={styles.removed}>
            <TransactionRuleActions
                rule={{ actions: segment.items }}
                counterparties={row.counterparties}
            />
        </div>
    );
}

export const RuleDetailsActionsCell = memo(
    ({ row }: { row: { original: RuleDetailsRowData } }) => {
        const data = row.original;
        const isUpdate = data.changeType === AuditLogChangeType.UPDATE;

        if (data.actionFieldDiffs.length > 0) {
            return (
                <ViewTableCell className={styles.td}>
                    <ActionFieldDiffItems
                        items={data.actionFieldDiffs}
                        row={data}
                    />
                </ViewTableCell>
            );
        }

        if (data.actionSegments !== null) {
            return (
                <ViewTableCell className={styles.td}>
                    <div className={styles.segmentStack}>
                        {data.actionSegments.map((seg, i) =>
                            renderActionSegment(data, seg, i),
                        )}
                    </div>
                </ViewTableCell>
            );
        }

        const hasPrev = isUpdate && data.prevActions.length > 0;
        const hasPrevText = isUpdate && data.prevActionTexts.length > 0;
        const hasCurrent =
            data.actions.length > 0 || data.actionTexts.length > 0;
        const showDash = isUpdate && !hasPrev && !hasPrevText && !hasCurrent;

        return (
            <ViewTableCell className={styles.td}>
                {showDash ? (
                    <span className={styles.emptyDash}>-</span>
                ) : (
                    <>
                        {hasPrev && (
                            <div className={styles.removed}>
                                <TransactionRuleActions
                                    rule={{ actions: data.prevActions }}
                                    counterparties={data.counterparties}
                                />
                            </div>
                        )}
                        {hasPrevText && (
                            <TextLines
                                lines={data.prevActionTexts}
                                className={styles.removedText}
                            />
                        )}
                        {data.actions.length > 0 && (
                            <TransactionRuleActions
                                rule={{ actions: data.actions }}
                                counterparties={data.counterparties}
                            />
                        )}
                        {data.actionTexts.length > 0 && (
                            <TextLines lines={data.actionTexts} />
                        )}
                    </>
                )}
            </ViewTableCell>
        );
    },
);

RuleDetailsActionsCell.displayName = "RuleDetailsActionsCell";
